import { EventBus, EVENTS } from '../../../../../shared/EventBus';
import {
  isThrowingStarAbility, THROWING_STAR_MAGAZINE, THROWING_STAR_RELOAD_MS, THROWING_STAR_READY_MS,
} from '../../../../../shared/combat/throwingStars';
import type { ActionContext } from '../types';
import { registerAbilitySessionReleaseHook } from '../AbilitySessionLifecycle';

type Player = ActionContext['currentPlayer'];
interface RackState {
  player: Player; playerId: string; used: number; readyAt: number; epoch: number;
  pending: Set<number>;
}
interface PendingStar { state: RackState; epoch: number; abilityId: string; playerId: string }
let stateByPlayer = new WeakMap<Player, RackState>();
const stateById = new Map<string, RackState>();
const pendingBySequence = new Map<number, PendingStar>();
let unregisterRelease: (() => void) | null = null;
let unsubscribeNetwork: Array<() => void> = [];

function clearPending(state: RackState): void {
  for (const seq of state.pending) pendingBySequence.delete(seq);
  state.pending.clear();
}

function settle(sequence: unknown, abilityId: string | undefined, rejected: boolean, ownerId?: string): RackState | null {
  if (typeof sequence !== 'number' || !Number.isSafeInteger(sequence)) return null;
  const pending = pendingBySequence.get(sequence);
  if (!pending || pending.abilityId !== abilityId) return null;
  const state = pending.state;
  if (ownerId && (ownerId !== state.player.id || ownerId !== pending.playerId)) return null;
  pendingBySequence.delete(sequence);
  state.pending.delete(sequence);
  if (pending.epoch !== state.epoch || state.playerId !== pending.playerId) return null;
  // Expired magazines are already refilled; an old rejection must never
  // refund a shot from the next rack, even before its first admission poll.
  if (state.readyAt > 0 && Date.now() >= state.readyAt) return null;
  if (rejected) {
    state.used = Math.max(0, state.used - 1);
    state.readyAt = 0;
    state.player.starBurstCount = state.used;
    state.player.isReloading = false;
    emitRackChanged(state.player, state, Date.now());
  }
  return state;
}

function ensureNetworkSubscriptions(): void {
  if (unsubscribeNetwork.length > 0) return;
  const bus = EventBus.getInstance();
  unsubscribeNetwork = [
    bus.on(EVENTS.NET_MESSAGE_PROJECTILE_LAUNCHED, (event) => {
      if (event.effect_index === 0) settle(event.client_cast_seq, event.ability_id, false, event.playerId);
    }),
    bus.on(EVENTS.NET_MESSAGE_PROJECTILE_REJECTED, (event) => {
      if (event.phase === 'cast' || event.phase === 'launch') settle(event.client_cast_seq, event.ability_id, true);
    }),
    bus.on(EVENTS.NET_MESSAGE_DEBUG_COMBAT, (event) => {
      // Bare legacy impact denials also use debugCombat. Only these reasons
      // identify a rejected cast without an explicit phase.
      if (!['cooldown', 'reloading'].includes(event.reason) && event.phase !== 'cast' && event.phase !== 'launch') return;
      const state = settle(event.clientCastSeq, event.abilityId ?? event.ability_id, true);
      if (state && event.reason === 'reloading' && typeof event.remainingMs === 'number'
          && Number.isFinite(event.remainingMs) && event.remainingMs > 0) {
        state.readyAt = Date.now() + event.remainingMs;
        state.player.isReloading = true;
        emitRackChanged(state.player, state, Date.now());
      }
    }),
  ];
}

/** Scoped disconnects preserve the rack on the surviving actor; logout drops all state. */
function release(playerId?: string): void {
  if (playerId) {
    const state = stateById.get(playerId);
    if (state) clearPending(state);
    stateById.delete(playerId);
    return;
  }
  stateById.clear();
  pendingBySequence.clear();
  stateByPlayer = new WeakMap();
  for (const unsubscribe of unsubscribeNetwork) unsubscribe();
  unsubscribeNetwork = [];
  unregisterRelease?.();
  unregisterRelease = null;
}

function getState(player: Player): RackState {
  let state = stateByPlayer.get(player);
  if (!state) {
    state = { player, playerId: player.id, used: 0, readyAt: 0, epoch: 0, pending: new Set() };
    stateByPlayer.set(player, state);
  }
  if (state.playerId !== player.id) {
    clearPending(state);
    if (stateById.get(state.playerId) === state) stateById.delete(state.playerId);
    state.playerId = player.id;
  }
  stateById.set(player.id, state);
  unregisterRelease ??= registerAbilitySessionReleaseHook(release);
  ensureNetworkSubscriptions();
  return state;
}

function emitReloadBeat(player: Player, effectId: string, duration: number): void {
  EventBus.getInstance().emit('vfx:spawn', {
    effectId, x: player.position.x, y: player.position.y,
    targetId: player.id, casterId: player.id, follow: true, duration,
    abilityId: 'assassin_auto',
  });
}

function emitRackChanged(player: Player, state: RackState, now = Date.now()): void {
  const reloading = state.readyAt > now;
  const used = (state.readyAt === 0 || reloading) ? state.used : 0;
  EventBus.getInstance().emit(EVENTS.THROWING_STARS_CHANGED, {
    playerId: player.id,
    used,
    remaining: THROWING_STAR_MAGAZINE - used,
    max: THROWING_STAR_MAGAZINE,
    reloading,
    readyAt: state.readyAt,
    reloadRemainingMs: Math.max(0, state.readyAt - now),
  });
}

function refresh(player: Player, state: RackState, now: number): void {
  if (state.readyAt > 0 && now >= state.readyAt) {
    clearPending(state);
    state.epoch += 1;
    state.used = 0;
    state.readyAt = 0;
    emitReloadBeat(player, 'reload_stars_complete', THROWING_STAR_READY_MS);
    emitRackChanged(player, state, now);
  }
  // Keep the existing character diagnostics honest for both entry points.
  player.starBurstCount = state.used;
  player.isReloading = state.readyAt > now;
}

/** Admission only: call before charging mana, starting cooldowns or allocating a cast. */
export function canFireThrowingStar(player: Player, abilityId: string, now = Date.now()): boolean {
  if (!isThrowingStarAbility(abilityId)) return true;
  const state = getState(player);
  refresh(player, state, now);
  return state.readyAt === 0;
}

/** Commit after a shot has successfully entered its launch timeline. No delayed timers. */
export function commitThrowingStar(player: Player, now = Date.now(), clientCastSeq?: number, abilityId = 'assassin_auto'): void {
  const state = getState(player);
  refresh(player, state, now);
  if (state.readyAt !== 0) return;
  if (clientCastSeq !== undefined && Number.isSafeInteger(clientCastSeq)) {
    if (pendingBySequence.has(clientCastSeq)) return;
    state.pending.add(clientCastSeq);
    pendingBySequence.set(clientCastSeq, { state, epoch: state.epoch, abilityId, playerId: player.id });
    // At most one magazine of unanswered predictions is retained.
    if (state.pending.size > THROWING_STAR_MAGAZINE) {
      const oldest = state.pending.values().next().value as number;
      state.pending.delete(oldest);
      pendingBySequence.delete(oldest);
    }
  }
  state.used += 1;
  if (state.used === THROWING_STAR_MAGAZINE) {
    state.readyAt = now + THROWING_STAR_RELOAD_MS;
    emitReloadBeat(player, 'reload_stars', THROWING_STAR_RELOAD_MS);
  }
  player.starBurstCount = state.used;
  player.isReloading = state.readyAt > now;
  emitRackChanged(player, state, now);
}

/** Pure snapshot for HUD/QA; readiness itself advances through the admission poll. */
export function getThrowingStarRackDiagnostics(player: Player, now = Date.now()) {
  const state = stateByPlayer.get(player);
  const reloading = !!state && state.readyAt > now;
  const used = state && (state.readyAt === 0 || reloading) ? state.used : 0;
  return { used, remaining: THROWING_STAR_MAGAZINE - used, reloading,
    readyAt: state?.readyAt ?? 0, reloadRemainingMs: Math.max(0, (state?.readyAt ?? 0) - now),
    pendingPredictions: state?.pending.size ?? 0 };
}
